import type { AlertDto } from '@vision360/contracts';
import { Check, CheckCheck, MoonStar } from 'lucide-react';
import { type KeyboardEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api/errors';
import { cn } from '@/lib/utils';
import { formatDuration } from '@/widgets/_sdk/format';
import { useAckAlert, useResolveAlert, useSnoozeAlert } from './api';

/** Ack, snooze (15 min · 1 h · 4 h · 24 h), resolve — shared by the row and the detail dialog. */

export const SNOOZE_MINUTES = [15, 60, 240, 1440] as const;

export const isActive = (a: AlertDto) => a.status !== 'resolved';
export const canAck = (a: AlertDto) => a.status === 'open' || a.status === 'snoozed';

const ACTION = 'h-9 pointer-coarse:h-11';

export function AlertActions({ alert, className }: { alert: AlertDto; className?: string }) {
  const { t } = useTranslation('alerts');
  const ack = useAckAlert();
  const snooze = useSnoozeAlert();
  const resolve = useResolveAlert();
  const [snoozing, setSnoozing] = useState(false);
  const groupId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const busy = ack.isPending || snooze.isPending || resolve.isPending;
  const error = ack.error ?? snooze.error ?? resolve.error;

  if (!isActive(alert)) return null;

  const closeSnooze = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    // Esc folds the durations only; the detail dialog skips its own Esc for this group.
    event.stopPropagation();
    setSnoozing(false);
    trigger.current?.focus();
  };

  return (
    <div className={cn('space-y-1', className)}>
      {/* biome-ignore lint/a11y/useSemanticElements: a toolbar of buttons, not a fieldset of inputs */}
      <div
        role="group"
        aria-label={t('actions.label', { title: alert.title })}
        className="flex flex-wrap items-center gap-2"
      >
        {canAck(alert) && (
          <Button
            size="sm"
            variant="outline"
            className={ACTION}
            disabled={busy}
            onClick={() => ack.mutate(alert.id)}
          >
            {ack.isPending ? <Spinner /> : <Check aria-hidden="true" className="h-4 w-4" />}
            {t('actions.ack')}
          </Button>
        )}
        <Button
          ref={trigger}
          size="sm"
          variant="outline"
          className={cn(ACTION, snoozing && 'border-primary/60 bg-primary/10')}
          disabled={busy}
          aria-expanded={snoozing}
          aria-controls={groupId}
          onClick={() => setSnoozing((s) => !s)}
        >
          {snooze.isPending ? <Spinner /> : <MoonStar aria-hidden="true" className="h-4 w-4" />}
          {t('actions.snooze')}
        </Button>
        {snoozing && (
          // biome-ignore lint/a11y/useSemanticElements: a set of buttons, not form inputs
          <div
            id={groupId}
            data-snooze-durations=""
            role="group"
            aria-label={t('actions.snoozeDurations')}
            className="flex animate-rise items-center gap-1 rounded-lg bg-muted/70 p-1"
            onKeyDown={closeSnooze}
          >
            {SNOOZE_MINUTES.map((minutes, i) => (
              <Button
                key={minutes}
                size="sm"
                variant="ghost"
                // The first duration takes focus as the group opens (keyboard path).
                autoFocus={i === 0}
                className={cn(ACTION, 'px-2.5 font-mono tabular-nums hover:bg-background')}
                aria-label={t('actions.snoozeFor', { duration: formatDuration(minutes * 60) })}
                disabled={busy}
                onClick={() =>
                  snooze.mutate(
                    { id: alert.id, untilMinutes: minutes },
                    { onSuccess: () => setSnoozing(false) },
                  )
                }
              >
                {formatDuration(minutes * 60)}
              </Button>
            ))}
          </div>
        )}
        <Button
          size="sm"
          variant="ghost"
          className={ACTION}
          disabled={busy}
          onClick={() => resolve.mutate(alert.id)}
        >
          {resolve.isPending ? <Spinner /> : <CheckCheck aria-hidden="true" className="h-4 w-4" />}
          {t('actions.resolve')}
        </Button>
      </div>
      <div aria-live="polite" className="text-xs text-destructive">
        {error &&
          (error instanceof ApiError && error.code === 'CONFLICT'
            ? t('actions.conflict')
            : t('actions.failed'))}
      </div>
    </div>
  );
}
